import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, NavigationHeader, SettingsItem, Spacer } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconPlus from 'design-system/icons/IconPlus';
import Text, { lineHeight } from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { editSavingsPlan } from 'features/pots/actions';
import DistributionCard from 'features/pots/components/DistributionCard';
import DistributionTotalCard, { distributionTotalCardHeight } from 'features/pots/components/DistributionTotalCard';
import DistributionTotalCardContent from 'features/pots/components/DistributionTotalCardContent';
import { selectPotProductByIdMap, selectSortedAutosavePots, selectUserCreatedInterestPots, selectVisibleAutosaveDistribution, } from 'features/pots/reducers/selectors';
import { getPeriodFromRRule } from 'features/pots/utils';
import Amplitude from 'utils/amplitude';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Sentry from '../../../../utils/sentry';
const checkDistributions = (autosaveDistribution, distribution) => {
    const filteredDistribution = Object.fromEntries(Object.entries(distribution).filter(([, v]) => !!v));
    const autosaveDistributionKeys = Object.keys(autosaveDistribution);
    let isSame = true;
    if (Object.keys(filteredDistribution).length === autosaveDistributionKeys.length) {
        autosaveDistributionKeys.every((key) => {
            isSame = autosaveDistribution[key] === filteredDistribution[key];
            return isSame;
        });
        return isSame;
    }
    return false;
};
const fetchConfig = {
    throws: true,
    muteResult: true,
};
const AutosaveDistribution = ({ navigation, route }) => {
    const colors = useColors();
    const pots = useAppSelector(selectUserCreatedInterestPots);
    const potProductByIdMap = useAppSelector(selectPotProductByIdMap);
    const potsLenRef = useRef(pots.length);
    const isEditFlow = route.params && 'isEditFlow' in route.params ? route.params?.isEditFlow : false;
    const createdPotId = route.params && 'createdPotId' in route.params ? route.params?.createdPotId : false;
    const isUnpauseFlow = route.params && 'isUnpauseFlow' in route.params ? route.params?.isUnpauseFlow : false;
    const [, , requesting, fetch] = useFetchOld(fetchConfig);
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const isAutosavePaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const collectionRRule = useAppSelector((state) => state.pots.savingsPlan?.collectionFrequency?.rrule);
    const autosaveDistribution = useAppSelector(selectVisibleAutosaveDistribution);
    const scrollY = useSharedValue(0);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        Amplitude.logEvent('Set Allocation', { type: 'autosave' });
    }, []);
    const [state, setState] = useState(() => {
        if (autosaveDistribution && Object.keys(autosaveDistribution).length) {
            const total = Math.round(Object.values(autosaveDistribution).reduce((sum, val) => sum + val, 0));
            return {
                distribution: autosaveDistribution,
                total,
            };
        }
        return {
            distribution: {},
            total: 0,
        };
    });
    const handleClear = useCallback(() => {
        setState({
            distribution: {},
            total: 0,
        });
    }, []);
    const [isDragging, setIsDragging] = useState(false);
    useEffect(() => {
        potsLenRef.current = pots.length;
    }, [pots.length]);
    useEffect(() => {
        if (!pots.length)
            return;
        const visiblePotIds = new Set(pots.map((pot) => pot.id));
        setState((currentState) => {
            const visibleDistribution = Object.fromEntries(Object.entries(currentState.distribution).filter(([potId]) => visiblePotIds.has(potId)));
            const total = Math.round(Object.values(visibleDistribution).reduce((sum, value) => sum + value, 0));
            if (total === currentState.total &&
                Object.keys(visibleDistribution).length === Object.keys(currentState.distribution).length) {
                return currentState;
            }
            return { distribution: visibleDistribution, total };
        });
    }, [pots]);
    const sortedPots = useAppSelector(selectSortedAutosavePots);
    const [initialSortedPots] = useState(sortedPots);
    const orderedPots = useMemo(() => {
        const initialIds = new Set(initialSortedPots.map((pot) => pot.id));
        const newPots = sortedPots.filter((pot) => !initialIds.has(pot.id)).reverse();
        const combinedPots = [...newPots, ...initialSortedPots];
        if (!createdPotId)
            return combinedPots;
        const createdPot = combinedPots.find((pot) => pot.id === createdPotId);
        if (!createdPot)
            return combinedPots;
        return [createdPot, ...combinedPots.filter((pot) => pot.id !== createdPotId)];
    }, [sortedPots, initialSortedPots, createdPotId]);
    const [headerInfoHeight, setHeaderInfoHeight] = useState(0);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const linesRef = useRef({});
    const updatedMeasurement = useCallback(() => {
        const measurements = linesRef.current;
        if (measurements.title !== undefined && measurements.subTitle !== undefined) {
            setHeaderInfoHeight(rem(8 + 4 + measurements.title * lineHeight.Text_24 + measurements.subTitle * lineHeight.TextThin_16));
        }
    }, []);
    const onTextLayoutTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.title === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            title: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const onTextLayoutSubTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.subTitle === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            subTitle: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const handlePercentageChange = useCallback((id, value) => {
        setState((oldState) => {
            const current = oldState.distribution[id] || 0;
            const othersTotal = oldState.total - current;
            const clamped = Math.round(Math.min(value, 100 - othersTotal));
            if (clamped === current)
                return oldState;
            return {
                total: othersTotal + clamped,
                distribution: { ...oldState.distribution, [id]: clamped },
            };
        });
    }, []);
    const handleContinue = useCallback(async () => {
        if (state.total === 100) {
            if (isEditFlow || isUnpauseFlow) {
                if (autosaveId && autosaveDistribution) {
                    if (isEditFlow && !createdPotId) {
                        const isEqual = checkDistributions(autosaveDistribution, state.distribution);
                        if (!isEqual) {
                            await fetch(editSavingsPlan(autosaveId, { distribution: state.distribution }));
                        }
                        navigation.goBack();
                    }
                    else {
                        const wasPaused = isAutosavePaused;
                        await fetch(editSavingsPlan(autosaveId, { isPaused: false, distribution: state.distribution }));
                        navigation.navigateDeprecated('Success', {
                            title: wasPaused ? 'Autosave has been enabled' : 'Autosave has been updated',
                            subTitle: 'Emma will start saving for you automatically. You can pause this at any time.',
                            buttonText: 'Done',
                            onContinue: () => {
                                navigation.pop(2);
                                return true;
                            },
                        });
                    }
                }
                else {
                    Sentry.logError({
                        message: 'Failed to continue',
                        filename: 'AutosaveDistribution',
                        err: 'Failed to continue from handleContinue in Edit flows. Not enough data',
                    });
                    navigation.goBack();
                }
                // Edit savings plan flow
            }
            else if (route.params && 'rules' in route.params) {
                if (route.params && 'overdraft' in route.params && typeof route.params.overdraft === 'boolean') {
                    navigation.navigateDeprecated('AutosaveSelectBank', {
                        rules: route.params.rules,
                        distribution: state.distribution,
                        overdraft: route.params.overdraft,
                        collectionFrequency: route.params.collectionFrequency,
                    });
                }
                else {
                    // Create savings plan flow
                    navigation.navigateDeprecated('AutosaveOverdraftSettings', {
                        distribution: state.distribution,
                        rules: route.params.rules,
                        collectionFrequency: route.params.collectionFrequency,
                    });
                }
            }
            else {
                Sentry.logError({
                    message: 'Failed to continue',
                    filename: 'AutosaveDistribution',
                    err: 'Failed to continue from handleContinue',
                });
            }
        }
    }, [
        autosaveDistribution,
        autosaveId,
        createdPotId,
        isAutosavePaused,
        isEditFlow,
        isUnpauseFlow,
        route.params,
        state.distribution,
        state.total,
    ]);
    const handleAddAPot = useCallback(() => {
        navigation.navigateDeprecated('SelectPotType');
    }, []);
    const title = useMemo(() => {
        if (createdPotId) {
            const period = collectionRRule ? `${getPeriodFromRRule(collectionRRule).toLowerCase()} ` : '';
            return `Edit your ${period}deposit allocation`;
        }
        return isEditFlow ? 'Edit your allocation' : 'Where do you want to build your balance?';
    }, [collectionRRule, createdPotId, isEditFlow]);
    const headerInputRange = useMemo(() => [0, headerInfoHeight], [headerInfoHeight]);
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader hasShadow={false} scrollY={scrollY} bgColorInputRange={headerInputRange} shadowInputRange={headerInputRange} withFrame/>
      <Animated.ScrollView scrollEnabled={!isDragging} scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <View style={styles.container}>
          <Text Text-24 onTextLayout={onTextLayoutTitle}>
            {title}
          </Text>
          <Spacer h={4}/>
          <Text TextThin-16 Secondary onTextLayout={onTextLayoutSubTitle}>
            {createdPotId ? 'You can allocate your savings into your new pot' : 'Split your savings across your pots'}
          </Text>
        </View>

        {headerInfoHeight ? (<Spacer h={distributionTotalCardHeight}/>) : (<DistributionTotalCardContent total={state.total} handleClear={handleClear}/>)}

        {!createdPotId && (<>
            <TouchableBounce onPress={handleAddAPot}>
              <View pointerEvents="box-only">
                <CardView noMarginTop noMarginBottom>
                  <SettingsItem title="Create new Pot" icon={<IconPlus color={colors.text.brand}/>}/>
                </CardView>
              </View>
            </TouchableBounce>
            <Spacer h={16}/>
          </>)}

        {orderedPots.map((pot) => (<DistributionCard key={pot.id} testID={`distribution-slider-${pot.id}`} name={pot.name} emoji={pot.emoji} percent={state.distribution[pot.id] || 0} maximumValue={(state.distribution[pot.id] || 0) + (100 - state.total)} onPercentageChange={(value) => handlePercentageChange(pot.id, value)} onSlidingStart={() => {
                setIsDragging(true);
            }} onSlidingComplete={() => {
                setIsDragging(false);
            }} productName={potProductByIdMap[pot.productId]?.name} productTierRates={potProductByIdMap[pot.productId]?.rates.tierRates}/>))}
      </Animated.ScrollView>
      {!!headerInfoHeight && (<DistributionTotalCard scrollY={scrollY} total={state.total} handleClear={handleClear} headerInfoHeight={headerInfoHeight}/>)}
      <Button brand floating title={isEditFlow ? 'Save' : 'Continue'} loading={requesting} onPress={handleContinue} disabled={state.total !== 100 ||
            (!createdPotId &&
                !isUnpauseFlow &&
                autosaveDistribution &&
                checkDistributions(autosaveDistribution, state.distribution))}/>
    </AppView>);
};
const styles = StyleSheet.create({
    container: {
        paddingBottom: rem(8),
        paddingHorizontal: rem(16),
    },
});
export default AutosaveDistribution;
